/** Working with data in the Builder: "Calculate / set values" (ADR-0103) and typed AI fields. */
export default {
  en: {
    calc: {
      title: "What it works out",
      why: "Values worked out without AI: sums, VAT, tolerances, dates, Hijri dates and text. Always the same answer for the same values.",
      value: "Value {{n}}",
      name: "Name",
      nameEn: "Name (English)",
      nameAr: "Name (Arabic)",
      savedAs: "Kept as",
      savedAsHelp: "Later steps use it by this name.",
      how: "How to work it out",
      pickRecipe: "Choose what to work out",
      formulaMode: "Write a formula",
      recipeMode: "Choose from a list",
      formula: "Formula",
      formulaHelp: "Values from earlier steps are written state.name. Functions: round, vat, with_vat, within, add_working_days, to_hijri, format_money, concat … Numbers use a dot for decimals.",
      add: "Add a value",
      remove: "Remove value {{n}}",
      moveUp: "Move value {{n}} up",
      preview: "In plain words",
      sample: "With sample values",
      sampleFrom: "From your last test run",
      sampleTyped: "Sample values you type",
      sampleValue: "Sample for ‘{{value}}’",
      result: "Result: {{value}}",
      resultEmpty: "Empty: a value it uses is missing.",
      cannot: "Can't be worked out with these values: {{reason}}",
      notAllowed: "This formula can't be used: {{reason}}",
      checking: "Working it out…",
      offline: "The sample result isn't available right now. The formula is still saved.",
      workingDays: "Working days follow your organisation's calendar when the agent runs (Sunday to Thursday in this preview).",
      onError: "If a value can't be worked out",
      onErrorFail: "Stop the run and say why",
      onErrorContinue: "Leave it empty and go on (the reason is kept)",
      technical: "Formula",
      kinds: {
        number: "a number",
        integer: "a whole number",
        date: "a date",
        text: "text",
        boolean: "yes or no",
        list: "a list",
        object: "a group of values",
        any: "a value",
      },
      makes: "Makes {{kind}}",
      groups: {
        numbers: "Numbers",
        money: "Money and VAT",
        compare: "Compare",
        dates: "Dates",
        hijri: "Hijri dates",
        text: "Text",
        set: "Set or copy a value",
      },
      recipes: {
        add: { name: "Add two values", say: "{{a}} plus {{b}}" },
        subtract: { name: "Subtract", say: "{{a}} minus {{b}}" },
        multiply: { name: "Multiply", say: "{{a}} times {{b}}" },
        divide: { name: "Divide", say: "{{a}} divided by {{b}}" },
        round: { name: "Round", say: "{{a}} rounded to {{places}} decimal places" },
        sum: { name: "Total of a list", say: "the total of {{field}} in {{list}}" },
        avg: { name: "Average of a list", say: "the average of {{field}} in {{list}}" },
        count: { name: "How many in a list", say: "how many items {{list}} has" },
        percent: { name: "As a percentage", say: "{{a}} as a percentage of {{b}}" },
        percent_of: { name: "Percentage of a value", say: "{{rate}}% of {{a}}" },
        percent_change: { name: "Change in percent", say: "the change from {{a}} to {{b}} in percent" },
        vat: { name: "VAT on an amount", say: "VAT at {{rate}}% on {{a}}" },
        with_vat: { name: "Amount with VAT", say: "{{a}} with {{rate}}% VAT added" },
        without_vat: { name: "Amount without VAT", say: "{{a}} without its {{rate}}% VAT" },
        vat_in: { name: "VAT included in an amount", say: "the {{rate}}% VAT included in {{a}}" },
        format_money: { name: "Write as money", say: "{{a}} written as {{currency}}" },
        within: { name: "Within a tolerance", say: "whether {{a}} is within {{tolerance}} of {{b}}" },
        today: { name: "Today's date", say: "today's date" },
        add_days: { name: "Add days", say: "{{a}} plus {{days}} days" },
        add_working_days: { name: "Add working days", say: "{{a}} plus {{days}} working days" },
        add_months: { name: "Add months", say: "{{a}} plus {{months}} months" },
        days_between: { name: "Days between two dates", say: "the days from {{a}} to {{b}}" },
        working_days_between: { name: "Working days between two dates", say: "the working days from {{a}} to {{b}}" },
        month_end: { name: "End of the month", say: "the last day of the month of {{a}}" },
        format_date: { name: "Write a date", say: "{{a}} written as {{pattern}}" },
        to_hijri: { name: "Hijri date", say: "{{a}} as a Hijri date" },
        format_hijri: { name: "Write a Hijri date", say: "{{a}} written as a Hijri date" },
        from_hijri: { name: "From a Hijri date", say: "the Gregorian date of Hijri {{a}}" },
        add_hijri_months: { name: "Add Hijri months", say: "{{a}} plus {{months}} Hijri months" },
        concat: { name: "Join text", say: "{{a}}, {{b}} and {{c}} joined" },
        pad: { name: "Reference number with zeros", say: "{{a}} padded with zeros to {{width}} digits" },
        upper: { name: "Capital letters", say: "{{a}} in capital letters" },
        lower: { name: "Small letters", say: "{{a}} in small letters" },
        trim: { name: "Remove extra spaces", say: "{{a}} without extra spaces" },
        replace: { name: "Replace text", say: "{{a}} with ‘{{find}}’ replaced by ‘{{with}}’" },
        format_number: { name: "Write a number", say: "{{a}} with {{places}} decimal places and thousands separators" },
        copy: { name: "Copy or rename a value", say: "the same as {{a}}" },
        constant: { name: "Set a fixed value", say: "always {{value}}" },
      },
      args: {
        a: "Value",
        b: "Second value",
        c: "Third value (optional)",
        places: "Decimal places",
        list: "The list",
        field: "Field to add up (optional)",
        rate: "Rate (%)",
        currency: "Currency",
        lang: "Language",
        tolerance: "Tolerance",
        toleranceHelp: "An amount (0.5), a percentage (2%) or, for dates, days (3 days).",
        days: "Days",
        months: "Months",
        pattern: "How to write it",
        width: "Digits",
        find: "Find",
        with: "Replace with",
        value: "Value",
        pick: "Pick a value from earlier steps",
        typed: "or type a value",
      },
      rates: { "5": "5% (UAE, Oman, Bahrain)", "15": "15% (Saudi Arabia)", other: "Other rate" },
      patterns: { "dd/MM/yyyy": "01/10/2026", "yyyy-MM-dd": "2026-10-01", "dd MMMM yyyy": "01 October 2026", "dddd dd MMMM yyyy": "Thursday 01 October 2026" },
      langs: { en: "English", ar: "Arabic" },
      written: "a written formula",
      unnamed: "this value",
      typedValue: "‘{{value}}’",
    },
    loop: {
      arrow: "for each item",
      action: "For each item of {{list}}: does {{count}} step(s)",
      actionAgent: "For each item of {{list}}: asks ‘{{agent}}’",
      theList: "a list",
      list: "The list",
      listHelp: "Each item of it is worked on by the steps, one child run per item. Inside the steps it is called ‘item’.",
      listPick: "Pick the list from earlier steps",
      what: "What to do for each item",
      whatStep: "New steps in this agent",
      whatAgent: "Another agent",
      firstKind: "The first step for each item",
      firstName: "Its name",
      firstNameEn: "Name of the step (English)",
      firstNameAr: "Name of the step (Arabic)",
      kinds: { ai: "Ask the AI", system: "Use a system", ask: "Ask a person", calc: "Calculate", agent: "Use another agent" },
      steps: "Steps done for each item",
      stepsHelp: "Add more on the ‘for each item’ arrow or after these steps in the graph. They only lead to each other.",
      stepsNone: "No steps yet: add one on the ‘for each item’ arrow.",
      agent: "The agent done for each item",
      how: "How the items are worked on",
      howWhy: "Limits keep a long list in check: how many at once, how many in all and what they may cost.",
      onError: "If an item fails",
      onErrorSkip: "Skip it, note why and go on",
      onErrorStop: "Stop: start no more items and fail the run",
      onErrorAsk: "Ask a person: skip it, try again or stop",
      maxItems: "At most this many items",
      maxItemsHelp: "A longer list stops the run with a plain reason instead of running away.",
      concurrency: "At the same time",
      cost: "The most all items may cost (US$)",
      costHelp: "Empty: what is left of the run's budget. Items not started when it is spent are marked ‘not run’.",
      collect: "What to keep from each item",
      collectAll: "Everything its steps made",
      collectHelp: "The results are a list in the order of the items, with how each went.",
      itemName: "Name of the item inside the steps",
      makes: "One result per item, in order, with how it went",
      progress: {
        title: "For each item · {{step}}",
        summary: "{{done}} done · {{failed}} failed · {{not_run}} not run (of {{total}})",
        running: "{{finished}} of {{total}} finished",
        item: "Item {{n}}",
        status: { done: "Done", failed: "Failed", skipped: "Skipped", not_run: "Not run" },
        budget: "the budget was spent",
        open: "See this item's run",
      },
    },
    table: {
      op: "What to do with the table",
      ops: { read: "Read a spreadsheet or CSV", match: "Match two lists", group: "Group and total" },
      opsWhat: {
        read: "Rows from an attached file (Excel or CSV) or from text or a list made earlier.",
        match: "Pairs rows of two tables on columns, the same value or within a tolerance; says what is left over on each side.",
        group: "One row per group (e.g. per supplier) with totals, counts or averages.",
      },
      from: "Where the table comes from",
      fromFile: "A file attached to the request",
      fromValue: "A value from earlier steps",
      document: "Part of the file's name (optional)",
      documentHelp: "Empty: the first attached spreadsheet or CSV with rows.",
      sheet: "Sheet (optional)",
      value: "The value",
      valueHelp: "Rows a system gave, or CSV text.",
      left: "First table",
      right: "Second table",
      keys: "Match on",
      keyLeft: "Column in the first table",
      keyRight: "Column in the second table",
      same: "The same value",
      within: "Within",
      tolerance: "Tolerance: 0.01, 2% or 3 days",
      addKey: "Add a column to match on",
      removeKey: "Remove match column {{n}}",
      mode: "Each row of the first table matches",
      oneToOne: "One row of the second table (the closest)",
      oneToMany: "Every row of the second table that fits",
      rows: "The rows",
      by: "Group by these columns",
      measures: "Totals",
      fn: { sum: "Total of", count: "How many", avg: "Average of", min: "Smallest", max: "Largest" },
      column: "Column",
      name: "Name of the total",
      addMeasure: "Add a total",
      removeMeasure: "Remove total {{n}}",
      columnsHelp: "Column names as in the file's first row, e.g. Amount.",
      makes: {
        read: "The rows of the table",
        match: "Matched pairs and what is left over on each side",
        group: "One row per group with its totals",
      },
      say: {
        read: "Reads the rows of {{from}}",
        readFile: "Reads the rows of the attached file",
        match: "Matches {{left}} with {{right}} on {{count}} column(s)",
        group: "Groups {{from}} with totals",
      },
    },
    within: {
      op: "is within",
      of: "of",
      tolerance: "Tolerance",
    },
    fields: {
      kind: "Kind of value",
      choices: "Possible answers",
      kinds: {
        text: "Text",
        number: "A number",
        money: "An amount of money",
        date: "A date",
        yes_no: "Yes or no",
        choice: "One of a list",
        list: "A list",
      },
      help: "The AI must answer each field in its kind; an answer that doesn't fit is sent back to the AI to fix.",
      fieldName: "Field {{name}}",
    },
  },
  ar: {
    calc: {
      title: "ما تحسبه",
      why: "قيم تُحسب دون ذكاء اصطناعي: مجاميع، ضريبة القيمة المضافة، هوامش السماح، تواريخ، تواريخ هجرية ونصوص. النتيجة نفسها دائماً للقيم نفسها.",
      value: "القيمة {{n}}",
      name: "الاسم",
      nameEn: "الاسم (بالإنجليزية)",
      nameAr: "الاسم (بالعربية)",
      savedAs: "تُحفظ باسم",
      savedAsHelp: "تستخدمها الخطوات اللاحقة بهذا الاسم.",
      how: "طريقة الحساب",
      pickRecipe: "اختر ما يجب حسابه",
      formulaMode: "اكتب معادلة",
      recipeMode: "اختر من قائمة",
      formula: "المعادلة",
      formulaHelp: "تُكتب القيم من الخطوات السابقة بالشكل state.name. الدوال: round وvat وwith_vat وwithin وadd_working_days وto_hijri وformat_money وconcat … وتُستخدم النقطة للكسور العشرية.",
      add: "إضافة قيمة",
      remove: "حذف القيمة {{n}}",
      moveUp: "نقل القيمة {{n}} إلى الأعلى",
      preview: "بكلمات بسيطة",
      sample: "بقيم تجريبية",
      sampleFrom: "من آخر تجربة",
      sampleTyped: "قيم تجريبية تكتبها",
      sampleValue: "قيمة تجريبية لـ‹{{value}}›",
      result: "النتيجة: {{value}}",
      resultEmpty: "فارغة: قيمة تستخدمها غير موجودة.",
      cannot: "لا يمكن حسابها بهذه القيم: {{reason}}",
      notAllowed: "لا يمكن استخدام هذه المعادلة: {{reason}}",
      checking: "جارٍ الحساب…",
      offline: "النتيجة التجريبية غير متاحة الآن. المعادلة محفوظة رغم ذلك.",
      workingDays: "تتبع أيام العمل تقويم مؤسستك عند تشغيل الوكيل (من الأحد إلى الخميس في هذه المعاينة).",
      onError: "إذا تعذّر حساب قيمة",
      onErrorFail: "أوقف التشغيل وبيّن السبب",
      onErrorContinue: "اتركها فارغة وتابع (يُحفظ السبب)",
      technical: "المعادلة",
      kinds: {
        number: "رقماً",
        integer: "عدداً صحيحاً",
        date: "تاريخاً",
        text: "نصاً",
        boolean: "نعم أو لا",
        list: "قائمة",
        object: "مجموعة قيم",
        any: "قيمة",
      },
      makes: "تنتج {{kind}}",
      groups: {
        numbers: "أرقام",
        money: "المال وضريبة القيمة المضافة",
        compare: "مقارنة",
        dates: "تواريخ",
        hijri: "تواريخ هجرية",
        text: "نصوص",
        set: "تعيين قيمة أو نسخها",
      },
      recipes: {
        add: { name: "جمع قيمتين", say: "{{a}} زائد {{b}}" },
        subtract: { name: "طرح", say: "{{a}} ناقص {{b}}" },
        multiply: { name: "ضرب", say: "{{a}} مضروبة في {{b}}" },
        divide: { name: "قسمة", say: "{{a}} مقسومة على {{b}}" },
        round: { name: "تقريب", say: "{{a}} مقرّبة إلى {{places}} منازل عشرية" },
        sum: { name: "مجموع قائمة", say: "مجموع {{field}} في {{list}}" },
        avg: { name: "متوسط قائمة", say: "متوسط {{field}} في {{list}}" },
        count: { name: "عدد عناصر قائمة", say: "عدد عناصر {{list}}" },
        percent: { name: "كنسبة مئوية", say: "{{a}} كنسبة مئوية من {{b}}" },
        percent_of: { name: "نسبة من قيمة", say: "{{rate}}٪ من {{a}}" },
        percent_change: { name: "التغيّر بالنسبة المئوية", say: "التغيّر من {{a}} إلى {{b}} بالنسبة المئوية" },
        vat: { name: "ضريبة القيمة المضافة على مبلغ", say: "ضريبة بنسبة {{rate}}٪ على {{a}}" },
        with_vat: { name: "المبلغ مع الضريبة", say: "{{a}} مع إضافة ضريبة {{rate}}٪" },
        without_vat: { name: "المبلغ دون الضريبة", say: "{{a}} دون ضريبته البالغة {{rate}}٪" },
        vat_in: { name: "الضريبة المضمّنة في مبلغ", say: "ضريبة {{rate}}٪ المضمّنة في {{a}}" },
        format_money: { name: "كتابة كمبلغ مالي", say: "{{a}} مكتوبة بعملة {{currency}}" },
        within: { name: "ضمن هامش سماح", say: "هل {{a}} ضمن {{tolerance}} من {{b}}" },
        today: { name: "تاريخ اليوم", say: "تاريخ اليوم" },
        add_days: { name: "إضافة أيام", say: "{{a}} زائد {{days}} يوماً" },
        add_working_days: { name: "إضافة أيام عمل", say: "{{a}} زائد {{days}} يوم عمل" },
        add_months: { name: "إضافة أشهر", say: "{{a}} زائد {{months}} شهراً" },
        days_between: { name: "الأيام بين تاريخين", say: "الأيام من {{a}} إلى {{b}}" },
        working_days_between: { name: "أيام العمل بين تاريخين", say: "أيام العمل من {{a}} إلى {{b}}" },
        month_end: { name: "نهاية الشهر", say: "آخر يوم في شهر {{a}}" },
        format_date: { name: "كتابة تاريخ", say: "{{a}} مكتوباً بالشكل {{pattern}}" },
        to_hijri: { name: "التاريخ الهجري", say: "{{a}} بالتاريخ الهجري" },
        format_hijri: { name: "كتابة تاريخ هجري", say: "{{a}} مكتوباً بالتاريخ الهجري" },
        from_hijri: { name: "من تاريخ هجري", say: "التاريخ الميلادي للتاريخ الهجري {{a}}" },
        add_hijri_months: { name: "إضافة أشهر هجرية", say: "{{a}} زائد {{months}} شهراً هجرياً" },
        concat: { name: "ضم نصوص", say: "ضم {{a}} و{{b}} و{{c}}" },
        pad: { name: "رقم مرجعي بأصفار", say: "{{a}} مع أصفار حتى {{width}} خانات" },
        upper: { name: "أحرف كبيرة", say: "{{a}} بأحرف كبيرة" },
        lower: { name: "أحرف صغيرة", say: "{{a}} بأحرف صغيرة" },
        trim: { name: "حذف المسافات الزائدة", say: "{{a}} دون مسافات زائدة" },
        replace: { name: "استبدال نص", say: "{{a}} مع استبدال ‹{{find}}› بـ‹{{with}}›" },
        format_number: { name: "كتابة رقم", say: "{{a}} بـ{{places}} منازل عشرية وفواصل الآلاف" },
        copy: { name: "نسخ قيمة أو إعادة تسميتها", say: "مثل {{a}}" },
        constant: { name: "تعيين قيمة ثابتة", say: "دائماً {{value}}" },
      },
      args: {
        a: "القيمة",
        b: "القيمة الثانية",
        c: "القيمة الثالثة (اختيارية)",
        places: "المنازل العشرية",
        list: "القائمة",
        field: "الحقل المراد جمعه (اختياري)",
        rate: "النسبة (٪)",
        currency: "العملة",
        lang: "اللغة",
        tolerance: "هامش السماح",
        toleranceHelp: "مبلغ (0.5)، أو نسبة مئوية (2%)، أو أيام للتواريخ (3 days).",
        days: "الأيام",
        months: "الأشهر",
        pattern: "طريقة الكتابة",
        width: "عدد الخانات",
        find: "ابحث عن",
        with: "استبدل بـ",
        value: "القيمة",
        pick: "اختر قيمة من الخطوات السابقة",
        typed: "أو اكتب قيمة",
      },
      rates: { "5": "5٪ (الإمارات وعُمان والبحرين)", "15": "15٪ (السعودية)", other: "نسبة أخرى" },
      patterns: { "dd/MM/yyyy": "01/10/2026", "yyyy-MM-dd": "2026-10-01", "dd MMMM yyyy": "01 أكتوبر 2026", "dddd dd MMMM yyyy": "الخميس 01 أكتوبر 2026" },
      langs: { en: "الإنجليزية", ar: "العربية" },
      written: "معادلة مكتوبة",
      unnamed: "هذه القيمة",
      typedValue: "‹{{value}}›",
    },
    loop: {
      arrow: "لكل عنصر",
      action: "لكل عنصر في {{list}}: ينفّذ {{count}} خطوة",
      actionAgent: "لكل عنصر في {{list}}: يسأل ‹{{agent}}›",
      theList: "قائمة",
      list: "القائمة",
      listHelp: "تعمل الخطوات على كل عنصر فيها، بتشغيل فرعي لكل عنصر. يُسمّى داخل الخطوات «item».",
      listPick: "اختر القائمة من الخطوات السابقة",
      what: "ما يُنفَّذ لكل عنصر",
      whatStep: "خطوات جديدة في هذا الوكيل",
      whatAgent: "وكيل آخر",
      firstKind: "أول خطوة لكل عنصر",
      firstName: "اسمها",
      firstNameEn: "اسم الخطوة (بالإنجليزية)",
      firstNameAr: "اسم الخطوة (بالعربية)",
      kinds: { ai: "اسأل الذكاء الاصطناعي", system: "استخدم نظاماً", ask: "اسأل شخصاً", calc: "احسب", agent: "استخدم وكيلاً آخر" },
      steps: "الخطوات المنفّذة لكل عنصر",
      stepsHelp: "أضف المزيد على سهم «لكل عنصر» أو بعد هذه الخطوات في المخطط. وهي تؤدي إلى بعضها فقط.",
      stepsNone: "لا خطوات بعد: أضف واحدة على سهم «لكل عنصر».",
      agent: "الوكيل المنفَّذ لكل عنصر",
      how: "طريقة العمل على العناصر",
      howWhy: "الحدود تُبقي القائمة الطويلة تحت السيطرة: كم عنصراً في الوقت نفسه، وكم عنصراً إجمالاً، وكم تكلفتها.",
      onError: "إذا فشل عنصر",
      onErrorSkip: "تخطَّه وسجّل السبب وتابع",
      onErrorStop: "أوقف: لا تبدأ عناصر أخرى وأفشل التشغيل",
      onErrorAsk: "اسأل شخصاً: تخطّيه أو إعادة المحاولة أو الإيقاف",
      maxItems: "هذا العدد من العناصر على الأكثر",
      maxItemsHelp: "القائمة الأطول توقف التشغيل بسبب واضح بدل أن تخرج عن السيطرة.",
      concurrency: "في الوقت نفسه",
      cost: "أقصى تكلفة لكل العناصر (دولار أمريكي)",
      costHelp: "فارغ: ما تبقّى من ميزانية التشغيل. العناصر التي لم تبدأ عند نفادها تُعلَّم «لم يُنفَّذ».",
      collect: "ما يُحفظ من كل عنصر",
      collectAll: "كل ما أنتجته خطواته",
      collectHelp: "النتائج قائمة بترتيب العناصر، مع حال كل عنصر.",
      itemName: "اسم العنصر داخل الخطوات",
      makes: "نتيجة لكل عنصر، بالترتيب، مع حاله",
      progress: {
        title: "لكل عنصر · {{step}}",
        summary: "{{done}} تم · {{failed}} فشل · {{not_run}} لم يُنفَّذ (من {{total}})",
        running: "انتهى {{finished}} من {{total}}",
        item: "العنصر {{n}}",
        status: { done: "تم", failed: "فشل", skipped: "تُخطّي", not_run: "لم يُنفَّذ" },
        budget: "نفدت الميزانية",
        open: "عرض تشغيل هذا العنصر",
      },
    },
    table: {
      op: "ماذا تفعل بالجدول",
      ops: { read: "قراءة جدول بيانات أو CSV", match: "مطابقة قائمتين", group: "التجميع والإجمالي" },
      opsWhat: {
        read: "صفوف من ملف مرفق (Excel أو CSV) أو من نص أو قائمة أُنتجت سابقاً.",
        match: "يقرن صفوف جدولين حسب أعمدة، بالقيمة نفسها أو ضمن هامش سماح؛ ويبيّن المتبقي في كل جانب.",
        group: "صف لكل مجموعة (مثل كل مورّد) مع مجاميع أو أعداد أو متوسطات.",
      },
      from: "مصدر الجدول",
      fromFile: "ملف مرفق بالطلب",
      fromValue: "قيمة من الخطوات السابقة",
      document: "جزء من اسم الملف (اختياري)",
      documentHelp: "فارغ: أول جدول بيانات أو CSV مرفق فيه صفوف.",
      sheet: "الورقة (اختياري)",
      value: "القيمة",
      valueHelp: "صفوف أعطاها نظام، أو نص CSV.",
      left: "الجدول الأول",
      right: "الجدول الثاني",
      keys: "المطابقة على",
      keyLeft: "عمود في الجدول الأول",
      keyRight: "عمود في الجدول الثاني",
      same: "القيمة نفسها",
      within: "ضمن",
      tolerance: "هامش السماح: 0.01 أو 2% أو 3 days",
      addKey: "إضافة عمود للمطابقة",
      removeKey: "حذف عمود المطابقة {{n}}",
      mode: "كل صف في الجدول الأول يطابق",
      oneToOne: "صفاً واحداً من الجدول الثاني (الأقرب)",
      oneToMany: "كل صف مناسب من الجدول الثاني",
      rows: "الصفوف",
      by: "التجميع حسب هذه الأعمدة",
      measures: "المجاميع",
      fn: { sum: "مجموع", count: "عدد", avg: "متوسط", min: "الأصغر", max: "الأكبر" },
      column: "العمود",
      name: "اسم الإجمالي",
      addMeasure: "إضافة إجمالي",
      removeMeasure: "حذف الإجمالي {{n}}",
      columnsHelp: "أسماء الأعمدة كما في الصف الأول من الملف، مثل Amount.",
      makes: {
        read: "صفوف الجدول",
        match: "الأزواج المتطابقة والمتبقي في كل جانب",
        group: "صف لكل مجموعة مع مجاميعها",
      },
      say: {
        read: "يقرأ صفوف {{from}}",
        readFile: "يقرأ صفوف الملف المرفق",
        match: "يطابق {{left}} مع {{right}} على {{count}} عمود",
        group: "يجمّع {{from}} مع المجاميع",
      },
    },
    within: {
      op: "ضمن",
      of: "من",
      tolerance: "هامش السماح",
    },
    fields: {
      kind: "نوع القيمة",
      choices: "الإجابات الممكنة",
      kinds: {
        text: "نص",
        number: "رقم",
        money: "مبلغ مالي",
        date: "تاريخ",
        yes_no: "نعم أو لا",
        choice: "واحد من قائمة",
        list: "قائمة",
      },
      help: "يجب أن يجيب الذكاء الاصطناعي عن كل حقل بنوعه؛ الإجابة التي لا تطابقه تُعاد إليه لتصحيحها.",
      fieldName: "الحقل {{name}}",
    },
  },
};
